import { Palette, Droplets, Sun, MessageCircle } from "lucide-react";
import { buildWhatsAppUrl } from "../../lib/constants";

// Nombres comerciales reales de catálogo — corregibles por el cliente
const PVC_COLORS = [
  { name: "Blanc", code: "Précontraint 502 — 8102", hex: "#F4F2ED" },
  { name: "Ivoire", code: "Précontraint 502 — 8106", hex: "#E8DFC8" },
  { name: "Sable", code: "Orchestra Max D314", hex: "#D8C49A" },
  { name: "Beige Lin", code: "Précontraint 502 — 8556", hex: "#C9B79B" },
  { name: "Terre de Sienne", code: "Sioen T2107", hex: "#B66A3C" },
  { name: "Rouge Basque", code: "Précontraint 502 — 8206", hex: "#9A2B2B" },
  { name: "Vert Foncé", code: "Sioen T5002", hex: "#2F5D3A" },
  { name: "Bleu Océan", code: "Précontraint 502 — 8402", hex: "#1E3A5F" },
  { name: "Gris Anthracite", code: "Précontraint 502 — 8807", hex: "#3A3A3C" },
  { name: "Noir", code: "Précontraint 502 — 8803", hex: "#0F0F10" },
];

const MESH_COLORS = [
  { name: "Desert Sand", code: "Commercial 95 — DS", hex: "#D6C9A8" },
  { name: "Sandstone", code: "Commercial 95 — SS", hex: "#C2A373" },
  { name: "Terracotta", code: "Commercial 95 — TC", hex: "#B65A3F" },
  { name: "Rainforest Green", code: "Commercial 95 — RG", hex: "#2E5E3A" },
  { name: "Electric Blue", code: "Commercial 95 — EB", hex: "#1F4C8C" },
  { name: "Silver Grey", code: "Commercial 95 — SG", hex: "#9AA0A6" },
  { name: "Charcoal", code: "Commercial 95 — CH", hex: "#3B3B3D" },
  { name: "Black", code: "Commercial 95 — BK", hex: "#0D0D0E" },
  { name: "Titanium White", code: "Commercial 95 — TW", hex: "#ECECEA" },
];

export default function Materials() {
  return (
    <section data-testid="materials-section" className="bg-white text-[#1F2937] section-pad">
      <div className="container-tight">
        <div className="flex items-center gap-3 mb-4">
          <span className="divider-accent" />
          <span className="kicker">Materiales</span>
        </div>
        <h2 className="font-display font-black tracking-tightest text-4xl md:text-5xl lg:text-6xl max-w-3xl">
          Materiales importados <span className="text-accent">de primer nivel</span>.
        </h2>
        <p className="mt-6 max-w-2xl text-[#1F2937]/70 text-base md:text-lg">
          Solo trabajamos con las mejores marcas del mundo.
        </p>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#F0F4DC]">
          {/* Malla sombra */}
          <div className="bg-eclipse p-8 md:p-10" data-testid="material-malla">
            <div className="flex items-center gap-3">
              <Sun className="w-6 h-6 text-accent" />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#1F2937]/60">Malla sombra arquitectónica</span>
            </div>
            <h3 className="mt-5 font-display font-black text-2xl tracking-tight">Protección UV 95%</h3>
            <ul className="mt-6 space-y-3 text-[#1F2937]/75 text-sm">
              <li>• Semi impermeable y ventilada</li>
              <li>• Variedad de colores y densidades</li>
              <li>• Resistente a rayos UV de alta intensidad</li>
              <li>• Vida útil hasta 20 años</li>
            </ul>
            <div className="mt-8 h-40 relative overflow-hidden border border-[#DDE2D5]">
              <img src="/images/galeria_velaria_cancha_02.jpg" alt="Malla sombra" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            </div>
          </div>

          {/* PVC */}
          <div className="bg-eclipse p-8 md:p-10" data-testid="material-pvc">
            <div className="flex items-center gap-3">
              <Droplets className="w-6 h-6 text-accent" />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#1F2937]/60">Membrana PVC impermeable</span>
            </div>
            <h3 className="mt-5 font-display font-black text-2xl tracking-tight">100% impermeable</h3>
            <ul className="mt-6 space-y-3 text-[#1F2937]/75 text-sm">
              <li>• <span className="text-[#1F2937]">Dickson</span> (Francia)</li>
              <li>• <span className="text-[#1F2937]">Sioen</span> (Bélgica)</li>
              <li>• <span className="text-[#1F2937]">Serge Ferrari</span> (Francia)</li>
              <li>• <span className="text-[#1F2937]">Polyfab</span> (Australia)</li>
              <li>• <span className="text-[#1F2937]">Verseidag</span> (Alemania)</li>
              <li>• Vida útil hasta 30 años</li>
            </ul>
            <div className="mt-8 h-40 relative overflow-hidden border border-[#DDE2D5]">
              <img src="/images/galeria_tensoestructura_05.jpg" alt="Membrana PVC" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            </div>
          </div>

          {/* Paleta — 2 sub-bloques */}
          <div className="bg-eclipse p-8 md:p-10" data-testid="material-colors">
            <div className="flex items-center gap-3">
              <Palette className="w-6 h-6 text-accent" />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#1F2937]/60">Paleta disponible</span>
            </div>
            <h3 className="mt-5 font-display font-black text-2xl tracking-tight">Nombres reales de catálogo</h3>
            <p className="mt-3 text-[#1F2937]/65 text-xs">Los colores mostrados son referencia digital. Solicita muestras físicas para ver el color real.</p>
          </div>
        </div>

        {/* Sub-bloque 1: Membrana PVC */}
        <div className="mt-16" data-testid="palette-pvc-block">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-6 bg-accent" />
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-accent">
              MEMBRANA PVC · SERGE FERRARI / SIOEN / DICKSON
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 md:gap-6">
            {PVC_COLORS.map((c) => <ColorChip key={c.code} color={c} block="pvc" />)}
          </div>
        </div>

        {/* Sub-bloque 2: Malla sombra */}
        <div className="mt-14" data-testid="palette-mesh-block">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-6 bg-accent" />
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-accent">
              MALLA SOMBRA · POLYFAB COMMERCIAL 95 / GALE PACIFIC
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 md:gap-6">
            {MESH_COLORS.map((c) => <ColorChip key={c.code} color={c} block="mesh" />)}
          </div>
        </div>

        <p className="mt-14 text-[#1F2937]/50 text-xs md:text-sm italic text-center md:text-left" data-testid="palette-footnote">
          Los colores mostrados son referencia digital. Solicita muestras físicas para ver el color real.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row gap-4 items-start">
          <a
            href={buildWhatsAppUrl("Hola, me gustaría recibir muestras físicas de colores disponibles.")}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-accent"
            data-testid="palette-cta-samples"
          >
            <MessageCircle className="w-4 h-4" /> Pide tus muestras físicas gratis
          </a>
        </div>
      </div>
    </section>
  );
}

function ColorChip({ color, block }) {
  const msg = `Hola, quiero ver muestra física del color ${color.name} (${color.code}).`;
  const isDark = (() => {
    // naive luminance
    const h = color.hex.replace("#", "");
    const r = parseInt(h.substr(0, 2), 16);
    const g = parseInt(h.substr(2, 2), 16);
    const b = parseInt(h.substr(4, 2), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) < 80;
  })();

  return (
    <a
      href={buildWhatsAppUrl(msg)}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col items-center text-center"
      data-testid={`chip-${block}-${color.name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="relative">
        <div
          className="w-[60px] h-[60px] md:w-[72px] md:h-[72px] rounded-full border transition-transform duration-300 group-hover:-translate-y-1 shadow-lg"
          style={{
            background: color.hex,
            borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)",
          }}
          aria-hidden="true"
        />
        <span
          className="absolute left-1/2 -translate-x-1/2 -bottom-2 translate-y-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap bg-accent text-white font-mono text-[10px] uppercase tracking-wider px-2 py-1 z-10"
          role="tooltip"
        >
          Pide una muestra →
        </span>
      </div>
      <p className="mt-4 font-sans text-xs text-[#1F2937] leading-tight">{color.name}</p>
      <p className="mt-1 font-mono text-[10px] text-[#1F2937]/50 leading-tight">{color.code}</p>
    </a>
  );
}
